// Image upload utilities with HEIC support
import heic2any from 'heic2any';

/**
 * Check if a file is HEIC format
 */
export const isHeicFile = (file) => {
  const heicTypes = ['image/heic', 'image/heif'];
  const heicExtensions = ['.heic', '.heif'];
  
  // Check MIME type
  if (heicTypes.includes(file.type?.toLowerCase())) {
    return true;
  }
  
  // Check file extension (sometimes MIME type is empty for HEIC)
  const fileName = file.name?.toLowerCase() || '';
  return heicExtensions.some(ext => fileName.endsWith(ext));
};

/**
 * Convert HEIC file to JPEG
 */
export const convertHeicToJpeg = async (file) => {
  try {
    const convertedBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.85
    });
    
    // heic2any can return an array or single blob
    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
    
    // Create a new File object with the converted blob
    const convertedFile = new File(
      [blob], 
      file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg'),
      { type: 'image/jpeg' }
    );
    
    return convertedFile;
  } catch (error) {
    console.error('HEIC conversion error:', error);
    throw new Error('Failed to convert HEIC image. Please try a different image format.');
  }
};

/**
 * Process an image file - converts HEIC to JPEG if needed, returns base64
 */
export const processImageFile = async (file, onProgress) => {
  return new Promise(async (resolve, reject) => {
    try {
      let processedFile = file;
      
      // Convert HEIC to JPEG if needed
      if (isHeicFile(file)) {
        if (onProgress) onProgress('Converting HEIC image...');
        processedFile = await convertHeicToJpeg(file);
      }
      
      // Read as base64
      const reader = new FileReader();
      
      reader.onload = () => {
        resolve(reader.result);
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read image file'));
      };
      
      reader.readAsDataURL(processedFile);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Process multiple image files with HEIC support
 */
export const processMultipleImages = async (files, maxImages = 10, onProgress) => {
  const results = [];
  const errors = [];
  
  for (let i = 0; i < Math.min(files.length, maxImages); i++) {
    try {
      if (onProgress) {
        onProgress(`Processing image ${i + 1} of ${files.length}...`);
      }
      
      const base64 = await processImageFile(files[i], onProgress);
      results.push(base64);
    } catch (error) {
      console.error(`Error processing file ${files[i].name}:`, error);
      errors.push({ file: files[i].name, error: error.message });
    }
  }
  
  return { images: results, errors };
};

/**
 * Accepted image formats for file input
 */
export const ACCEPTED_IMAGE_FORMATS = 'image/*,.heic,.heif';
